import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useState } from "react";
import HomeLink from "../HomeLink";
import PluginPage from "../plugin-page/plugin.page";

interface PluginInfo {
  id: string;
  name: string;
}

const PLUGINS: PluginInfo[] = [
  { id: "19015", name: "AuctionGUI+" },
  { id: "15007", name: "Christmas+" },
  { id: "2604", name: "Gangs+" },
  { id: "6515", name: "ShopGUI+" },
  { id: "37707", name: "PlayerShopGUI+" },
];

const NAV_LINKS = [
  {
    title: "Docs",
    desc: "Visit our docs for all of plugins.",
    url: "https://docs.brcdev.net/",
  },
  {
    title: "Feedback",
    desc: "Vote on which features should be added first.",
    url: "https://feedback.brcdev.net/",
  },
  {
    title: "Discord",
    desc: "Join our Discord server to get support.",
    url: "https://discord.brcdev.net",
  },
  {
    title: "Purchase",
    desc: "Buy our plugins available at SpigotMC marketplace.",
    url: "https://www.spigotmc.org/resources/authors/brc-plugins.2348534/",
  },
];

export const HomePage = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedPlugin, setSelectedPlugin] = useState<PluginInfo | null>(null);

  const handleOpenPlugin = (plugin: PluginInfo) => {
    setSelectedPlugin(plugin);
    onOpen();
  };

  return (
    <>
      <Container maxW="6xl" mt={{ base: 0, md: 24 }} p={0}>
        {/* Hero Section */}
        <Box
          px={12}
          py={20}
          bg="brand.300"
          bgGradient="linear(to-r, brand.300, brand.500)"
          color="white"
          borderRadius={{ base: 0, md: 4 }}
          mb={4}
        >
          <Heading
            fontWeight="600"
            size={{ base: "xl", md: "2xl" }}
            mb={2}
            textTransform="capitalize"
          >
            BRC Plugins Home Page
          </Heading>
          <Text fontSize="lg">
            Welcome to BRC Plugins home page! Here you can find all the useful links and details for our products.
          </Text>
        </Box>

        {/* Navigation Links */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} mb={6}>
          {NAV_LINKS.map((link) => (
            <HomeLink
              key={link.title}
              title={link.title}
              desc={link.desc}
              url={link.url}
            />
          ))}
        </SimpleGrid>

        {/* Plugin Details Modals Section */}
        <Box
          flex="1"
          p={8}
          bg="gray.50"
          color="gray.600"
          borderWidth="1px"
          borderColor="gray.300"
          borderRadius={{ base: 0, md: 4 }}
        >
          <Flex
            h="100%"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box mb={4}>
              <Heading size={{ base: "md", md: "lg" }} mb={2}>
                View Plugin Details
              </Heading>
              <Text>Use the buttons below to preview plugin pages</Text>
            </Box>
            <Stack direction={{ base: "column", sm: "row" }} wrap="wrap">
              {PLUGINS.map((plugin) => (
                <Button
                  key={plugin.id}
                  colorScheme="brand"
                  onClick={() => handleOpenPlugin(plugin)}
                >
                  {plugin.name}
                </Button>
              ))}
            </Stack>
          </Flex>
        </Box>
      </Container>

      {/* Dynamic Plugin Modal */}
      <Modal size="6xl" isOpen={isOpen} onClose={onClose} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader display="flex" alignItems="center" gap="0.5rem">
            {selectedPlugin?.name}{" "}
            <Text fontSize="md" color="gray.700" fontWeight="200">
              Preview of SpigotMC Plugin page
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedPlugin && <PluginPage pluginId={selectedPlugin.id} />}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default HomePage;